/**
 * Display formatters shared by every screen. Money is stored in millimes
 * (thousandths of a TND) across the API — dividing by 1000 keeps the numbers
 * operators already see today (docs/03).
 */

export function money(value: string | number | null | undefined, digits = 3): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? (n / 1000).toFixed(digits) : String(value);
}

export function millimes(value: string | number | null | undefined): string {
  return money(value, 3);
}

export function tnd(value: string | number | null | undefined): string {
  const formatted = money(value, 3);
  return formatted === '—' ? formatted : `${formatted} TND`;
}

/**
 * Canonical TND display for DINAR amounts (NOT millimes). Null-safe with a
 * configurable fallback so "no value" and "zero dinars" never render the same
 * (review: LogisticsAnalyticsPage returned '0,000 DT' for null).
 * This is the ONE definition — delete any local formatTnd copy.
 */
export function formatTnd(val: number | null | undefined, fallback = '—'): string {
  if (val === null || val === undefined || Number.isNaN(val)) return fallback;
  return `${val.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT`;
}

export function int(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('fr-FR');
}

export function pct(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined) return '—';
  return `${(value * 100).toFixed(digits)} %`;
}

export function score(value: string | number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(digits) : String(value);
}

export function stamp(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('fr-FR');
}

/**
 * Capitalizes the first letter of each word and lowercases the remainder (Title Case / Capitalized Case).
 * Preserves hyphens (e.g. "Acne-Pointgel"), alphanumeric unit tokens (e.g. "10ml", "240ml"),
 * and single uppercase letters (e.g. "T" in "Pharmaceris T").
 *
 * Examples:
 * - "PHARMACERIS T Medi Acne-pointgel 10ml" -> "Pharmaceris T Medi Acne-Pointgel 10ml"
 * - "pharmaceris t medi acne pointgel 10 ml" -> "Pharmaceris T Medi Acne Pointgel 10 Ml"
 * - "BIO ORIENT Huile De Pepin..." -> "Bio Orient Huile De Pepin..."
 */
export function toCapitalizedCase(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  return value.replace(/[\p{L}\p{N}]+/gu, (word) => {
    // Alphanumeric units like 10ml, 250ml, 50g, 400mg
    const unitMatch = /^(\d+)([a-zA-Z]+)$/.exec(word);
    if (unitMatch?.[1] && unitMatch?.[2]) {
      return unitMatch[1] + unitMatch[2].toLowerCase();
    }
    // Single letter tokens (e.g. "T" in Pharmaceris T)
    if (word.length === 1) {
      return word.toUpperCase();
    }
    // Standard words: capitalize first letter, lowercase the rest
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
}


/** Scan-console success/warning/error beep (Web Audio, autoplay-safe). */
export function playBeep(type: 'success' | 'warning' | 'error') {
  try {
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();

    if (type === 'success') {
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();

      osc1.type = 'sine';
      osc2.type = 'sine';
      osc1.frequency.setValueAtTime(880, ctx.currentTime);
      osc2.frequency.setValueAtTime(1760, ctx.currentTime + 0.08);

      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.25);

      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(ctx.destination);

      osc1.start(ctx.currentTime);
      osc1.stop(ctx.currentTime + 0.08);
      osc2.start(ctx.currentTime + 0.08);
      osc2.stop(ctx.currentTime + 0.25);
    } else {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(220, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(140, ctx.currentTime + 0.3);

      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    }
  } catch {
    // Autoplay policy fallback
  }
}
